/* eslint-disable complexity */
import { RoundedPlusButton } from "@faire/retailer-visitor-shared/components/Button/RoundedPlusButton";
import { selectBadge } from "@faire/retailer-visitor-shared/components/ProductBadge/badgeUtils";
import { QuickAdd } from "@faire/retailer-visitor-shared/components/QuickAdd/index";
import { LazyEncapsulatedQuickAdd } from "@faire/retailer-visitor-shared/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { RoundNotifyMeButton } from "@faire/retailer-visitor-shared/components/RoundNotifyMeButton/index";
import { PAGE_TYPE } from "@faire/retailer-visitor-shared/consts/PAGE_TYPE";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import {
  IProductTileBadgeConfigurationProp,
  withConfigurationConsumer,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/__internal__/configurableProductTileUtils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ProductTileOverlay } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/ProductTileOverlay";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productTileOverlayProductTileAdaptor } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/productTileOverlayUtils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productClickForSearchPageParametersILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import {
  IProductImageProps,
  ProductImage,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductImage";
import { ProductTileInfo } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTileInfo";
import { RetailInfo } from "@faire/retailer-visitor-shared/features/MarketplaceItems/RetailInfo";
import {
  ImageContainer,
  NotifyMeWrapper,
  ProductError,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/styled";
import { TopBadges } from "@faire/retailer-visitor-shared/features/MarketplaceItems/TopBadges";
import { IProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/ProductAddOptions";
import { useCartProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/useCartProductAddOptions";
import { SaveToBoard } from "@faire/retailer-visitor-shared/features/RetailerBoards/Components/SaveToBoard";
import {
  SaveToBoardContext,
  SaveToBoardProvider,
} from "@faire/retailer-visitor-shared/features/RetailerBoards/Context";
import { usePrefetchOnHover } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnHover";
import { usePrefetchOnTouch } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnTouch";
import { useProductTileClickImpressionContext } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressionContext";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useProductTileViewImpression } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileViewImpression";
import { filterInvalidImages } from "@faire/retailer-visitor-shared/lib/filterInvalidImages";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useCanAddToCart } from "@faire/retailer-visitor-shared/lib/useCanAddToCart";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { UpdateCartHandler } from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { useMarketCurrentEvent } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketCurrentEvent";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { usePreloadNavigationToPDP } from "@faire/retailer-visitor-shared/views/product/preload/preloadNavigationToPDP";
import { usePreloadClickToPDP } from "@faire/retailer-visitor-shared/views/product/preload/usePreloadClickToPDP";
import { isFastly } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { TelemetryManager } from "@faire/web/common/opentelemetry/TelemetryManager";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import { Link } from "@faire/web/ui/LinkAnchor";
import { trackEventsUntrackedproducttileView } from "@faire/web-api--source/events/events/view/untrackedproducttile";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IProduct as IProductNotLean } from "@faire/web-api--source/indigofair/data/IProduct";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { Span } from "@opentelemetry/api";
import { useQuery } from "@tanstack/react-query";
import { Location } from "history";
import React, { Suspense, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

export interface ProductLinkProps {
  productUrl: string;
  replace?: boolean;
}

export interface ProductTileProps extends IProductTileBadgeConfigurationProp {
  productTile: ProductTileInfo | undefined;
  generateProductLinkProps?: (
    productToken: string,
    location: Location,
    brandToken?: string,
    productOptionToken?: string
  ) => ProductLinkProps | undefined;
  className?: string;
  hideDetails?: boolean;
  hideInfo?: boolean;
  onProductClick?: (productToken: string) => void;
  onQuickAddClick?: (newQuantity?: number) => void;
  onChooseOption?: () => void;
  imageDimension?: IProductImageProps["imageDimension"];
  newTab?: boolean;
  productAddOptions?: IProductAddOptions;
  showVerticalImagery?: boolean;
  brandName?: string;
  showPlusButton?: boolean;
  "data-test-id"?: string;
  isInvoice?: boolean;
  showEncapsulatedQuickAdd?: boolean;
  altTag?: string;
  updateCartFn?: UpdateCartHandler;
  isSubscriedToBackInStock?: boolean;
  deleteBackInStockNotification?: (productOptionToken: string) => void;
  hideAddToCartAndNotifyMe?: boolean;
  hideSaveToBoard?: boolean;
  allowLinkInterception?: boolean;
}

const ProductTileBase: React.FC<ProductTileProps> = (props) => {
  const {
    productTile,
    altTag,
    updateCartFn,
    hideSaveToBoard,
    allowLinkInterception,
  } = props;

  const productTileSpan = TelemetryManager.get().getManagedSpan(
    TelemetryManager.get().startManagedSpan(
      "ProductTile",
      {
        productToken: productTile?.product?.token,
      },
      undefined
    ) ?? ""
  );

  const imageLoadSpan: Span | undefined = TelemetryManager.get().startSpan(
    "ProductTileImage",
    undefined,
    productTileSpan ?? undefined
  );

  React.useEffect(() => {
    if (productTileSpan) {
      // End the span when the component finishes rendering
      TelemetryManager.get().endSpan(productTileSpan);
    }
  }, [productTileSpan]);

  const { strictLocalize } = useStrictLocalization();
  const canAddToCart = useCanAddToCart();

  const canAdd = props.isInvoice || canAddToCart;

  const imageContainerRef = useRef<HTMLDivElement>(null);

  const trackProductTileProps = useProductTileViewImpression({
    productToken: productTile?.product?.token,
    promotedBy: productTile?.product?.promoted_by,
    impressionId: productTile?.product?.impression_id,
  });

  const leanProductTile = !productTile
    ? undefined
    : productClickForSearchPageParametersILeanProductTileAdapter(
        ILeanProductTile.build({
          ...productTile,
          product: !productTile.product
            ? undefined
            : ILeanProductTile.ILeanProduct.build({
                ...productTile.product,
              }),
        })
      );

  const {
    recordProductTileClickEvent,
    recordProductBadgeViewEvent,
    recordChooseOptionClickEvent,
    recordQuickAddSubmitEvent,
    recordQuickAddButtonClickEvent,
    recordSaveToBoardSubmitEvent,
    recordHeartClickedEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
    recordProductTileMiddleClickEvent,
    recordProductTileRightClickEvent,
    recordProductTileTouchStartEvent,
  } = useProductTileClickImpressions(leanProductTile, undefined);

  const {
    isPending: validateImageLoading,
    isError: validateImageError,
    data: validateImageData,
  } = useQuery({
    queryKey: ["validate-image-result", productTile?.best_image],
    queryFn: async () => {
      const image = productTile?.best_image;
      if (image?.url && !isFastly(image.url)) {
        return await filterInvalidImages([image]);
      } else {
        return [];
      }
    },
  });

  const location = useLocation();
  const retailerToken = getRetailer()?.token;
  const { productTileContainer } = useProductTileClickImpressionContext();
  React.useEffect(() => {
    if (
      productTileContainer ===
      ProductTileContainer.PRODUCT_TILE_CONTAINER_UNKNOWN
    ) {
      trackEventsUntrackedproducttileView(
        retailerToken ?? "",
        productTile?.product?.token ?? "",
        location.pathname + location.search
      );
    }
  }, [
    productTileContainer,
    productTile?.product?.token,
    retailerToken,
    location.pathname,
    location.search,
  ]);

  const cartProductAddOptions = useCartProductAddOptions();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const preloadNavigationToPDP = usePreloadNavigationToPDP();
  const clientPreloadNavigationToPDP = usePreloadClickToPDP();

  const productAddOptions = props.productAddOptions ?? cartProductAddOptions;

  const { productUrl, replace }: ProductLinkProps = useMemo(() => {
    const productToken = productTile?.product?.token;

    if (!productToken) {
      return { productUrl: faireBaseUrl() };
    }
    // For SEO discoverability, expose "real" product page links instead of a signup brand page link for bot.
    if (isPrerender()) {
      return { productUrl: ProductPagePaths.index(productToken) };
    }

    const selectedProductOptionToken =
      productTile?.selected_product_option_token;
    const brandToken = productTile?.product?.brand_token;
    return (
      props.generateProductLinkProps?.(
        productToken,
        location,
        brandToken,
        selectedProductOptionToken
      ) ?? {
        productUrl: ProductPagePaths.index(productToken, {
          productOptionToken: selectedProductOptionToken,
        }),
      }
    );
  }, [
    productTile?.product?.token,
    productTile?.product?.brand_token,
    productTile?.selected_product_option_token,
    props,
    location,
  ]);

  const { onMouseEnter, onMouseLeave } = usePrefetchOnHover(
    {
      productToken: productTile?.product?.token,
      bestImage: productTile?.best_image,
      images: productTile?.images ?? [],
      brandToken: productTile?.product?.brand_token,
    },
    productUrl
  );

  const { onTouchStart } = usePrefetchOnTouch(
    {
      productToken: productTile?.product?.token,
      bestImage: productTile?.best_image,
      images: productTile?.images ?? [],
      brandToken: productTile?.product?.brand_token,
    },
    productUrl
  );

  const prefetchPDPDataOnMouseDown = () => {
    if (!productTile) {
      return;
    }

    if (productTile.product?.token) {
      clientPreloadNavigationToPDP(productTile.product.token);
    }

    preloadNavigationToPDP(
      {
        productToken: productTile.product?.token,
        brandToken: productTile.product?.brand_token,
        bestImage: productTile.best_image,
        images: productTile.images,
        showVerticalImagery: props.showVerticalImagery,
      },
      productUrl
    );
  };

  const handleClick = useEffectEvent(() => {
    recordProductTileClickEvent();
    if (productTile) {
      preloadNavigationToPDP(
        {
          productToken: productTile.product?.token,
          brandToken: productTile.product?.brand_token,
          bestImage: productTile.best_image,
          images: productTile.images,
          showVerticalImagery: props.showVerticalImagery,
        },
        productUrl
      );
    }

    if (productTile?.product?.token) {
      props.onProductClick?.(productTile.product.token);
    }
  });

  const handleTouchStart = () => {
    recordProductTileTouchStartEvent();
    onTouchStart();
  };

  const handleOnAuxClick = useEffectEvent(
    <T extends HTMLElement>(event: React.MouseEvent<T>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );

  const handleChooseOption = () => {
    recordChooseOptionClickEvent();
    props.onChooseOption?.();
  };

  const handleQuickAdd = (newQuantity?: number) => {
    recordQuickAddButtonClickEvent();
    props.onQuickAddClick?.(newQuantity);
  };

  const primaryImage = useMemo(() => {
    const image = props.productTile?.best_image;
    if (image?.url && isFastly(image.url)) {
      return image;
    } else if (
      validateImageLoading ||
      validateImageError ||
      !validateImageData
    ) {
      return PLACEHOLDER_IMAGE;
    } else {
      return validateImageData[0] ?? PLACEHOLDER_IMAGE;
    }
  }, [
    validateImageError,
    validateImageLoading,
    props.productTile?.best_image,
    validateImageData,
  ]);

  const productName = props.productTile?.product?.name;

  const isSalesPaused =
    productTile?.product?.state === IProductNotLean.State.SALES_PAUSED;

  const availableUnits =
    props.productTile?.quick_add?.quick_add_option?.option_available_units ??
    Number.POSITIVE_INFINITY;

  const canQuickAddProduct =
    (canAdd || getIsBrandPreview()) && !isSalesPaused && availableUnits !== 0;

  const canSaveToBoard =
    isLoggedInRetailer && !props.hideDetails && !hideSaveToBoard;

  const saveToBoardContext = React.useContext(SaveToBoardContext);

  const topBadge = useMemo(() => {
    const productBadges = productTile?.badge_list?.badges ?? [];
    return selectBadge(productBadges, "TOP");
  }, [productTile?.badge_list?.badges]);

  const { isLive } = useMarketCurrentEvent();
  const renderExtraContent = () => {
    const extraElements = [];
    if (!productTile?.product) {
      return null;
    }

    const hideMarketBadge = props.configuration?.disableEventBadge && isLive;
    const showEventBadge =
      !hideMarketBadge &&
      productTile?.is_highlighted_product_for_promotional_event;

    const quickAddProductOptionToken =
      productTile?.quick_add?.quick_add_option?.option_token;
    extraElements.push(
      <TopBadges
        key="top-badges"
        tileBadge={topBadge}
        onTopLeftBadgeVisible={recordProductBadgeViewEvent}
        favoriteButton={
          canSaveToBoard ? (
            <SaveToBoardProvider
              value={{
                trackOrigin: saveToBoardContext.trackOrigin ?? "brand",
              }}
            >
              <StyledSaveToBoard
                data-test-id="favorite-button"
                key={`${productTile.product.token}-favorite`}
                productToken={productTile.product.token}
                productImage={productTile.best_image}
                boardTokens={productTile.product.board_tokens}
                brandToken={productTile.product.brand_token}
                onHeartClicked={recordHeartClickedEvent}
                onSaveToBoard={recordSaveToBoardSubmitEvent}
                onRemoveFromBoard={recordRemoveFromBoardSubmitEvent}
              />
            </SaveToBoardProvider>
          ) : null
        }
        showEventBadge={showEventBadge}
        hideDetails={props.hideDetails}
      />
    );

    if (props.hideAddToCartAndNotifyMe) {
      return extraElements.length > 0 ? extraElements : null;
    }

    if (props.isInvoice || (canQuickAddProduct && productTile?.quick_add)) {
      if (props.isInvoice || props.showEncapsulatedQuickAdd) {
        extraElements.push(
          <Suspense
            fallback={null}
            key={`${productTile.product?.token}-suspense`}
          >
            <LazyEncapsulatedQuickAdd
              key={`${productTile.product?.token}-encapsulated-quick-add`}
              productToken={productTile.product?.token}
              quickAdd={productTile.quick_add}
              brandToken={productTile.product?.brand_token}
              imageContainerRef={imageContainerRef}
              updateItems={updateCartFn}
              onQuickAddClick={handleQuickAdd}
              onQuickAddSubmit={recordQuickAddSubmitEvent}
              onChooseOption={handleChooseOption}
              onChooseOptionQuantityUpdate={
                recordChooseOptionQuantityUpdateSubmitEvent
              }
            />
          </Suspense>
        );
      } else {
        if (props.showPlusButton) {
          extraElements.push(
            <PlusButton
              key={`${productTile.product?.token}-plus-btn`}
              data-test-id="quick-add-marketplace-product"
              onClick={(e) => {
                e.preventDefault();
                props.onQuickAddClick?.();
              }}
            />
          );
        } else if (productTile?.quick_add) {
          extraElements.push(
            <DesktopQuickAdd
              key={`${productTile.product?.token}-quick-add`}
              productToken={productTile.product.token}
              brandToken={productTile.product.brand_token}
              quickAddData={productTile.quick_add}
              onQuickAddOption={handleQuickAdd}
              onChooseOption={handleChooseOption}
              forceShowModal={
                !!productTile.product.per_style_min_order_quantity
              }
              productAddOptions={productAddOptions}
              style={{
                bottom: "10px",
              }}
              isHighlightedProductForPromotionalEvent={
                productTile.is_highlighted_product_for_promotional_event
              }
              updateCartFn={updateCartFn}
            />
          );
        }
      }
    } else if (
      isSalesPaused &&
      quickAddProductOptionToken !== undefined &&
      isLoggedInRetailer
    ) {
      extraElements.push(
        <NotifyMeWrapper
          onClick={(e) => e.preventDefault()}
          key={`${productTile.product?.token}-round-notify-me-picker`}
        >
          <RoundNotifyMeButton
            productOptionToken={quickAddProductOptionToken}
            isSubscriedToBackInStock={props.isSubscriedToBackInStock}
            deleteBackInStockNotification={props.deleteBackInStockNotification}
          />
        </NotifyMeWrapper>
      );
    }

    return extraElements.length > 0 ? extraElements : null;
  };

  const userStore = useUserStore();
  const renderErrorPlaceholder = () => {
    if (userStore.isEmployee) {
      return (
        <ProductError>
          Missing options or image or name for product{" "}
          {props.productTile?.product?.token}
        </ProductError>
      );
    }
    return null;
  };

  const settingsAssignmentRef = useDoActionWhenVisible(() => {
    productTile?.settings_to_assign_on_view.forEach((setting) => {
      assignSettingOnEvent(setting);
    });
  });

  if (props.productTile === undefined || !primaryImage || !productName) {
    return renderErrorPlaceholder();
  }

  const ProductLink = allowLinkInterception
    ? AnchorProductLink
    : RegularProductLink;
  const productLinkProps = {
    to: productUrl,
    replace,
    ...(allowLinkInterception ? { href: productUrl } : {}),
  };

  //TODO: @z27fangFaire change all product image section to use `ProductImage` instead of a carousel.
  return (
    // trackProductTileProps should ALWAYS wrap the outermost div of the product tile
    <Container
      {...trackProductTileProps}
      onAuxClick={handleOnAuxClick}
      ref={(element) => {
        trackProductTileProps?.ref?.(element);
        settingsAssignmentRef(element);
      }}
    >
      <ProductLink
        key={`${props.productTile.product?.token}-product-tile`}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onMouseDown={prefetchPDPDataOnMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={props.className}
        data-test-id={props["data-test-id"] ?? "product-tile"}
        target={props.newTab ? "_blank" : undefined}
        {...productLinkProps}
      >
        <ImageContainer ref={imageContainerRef}>
          <ProductImage
            image={primaryImage}
            imageDimension={props.imageDimension}
            showVerticalImagery={props.showVerticalImagery}
            alt={
              altTag ??
              getAltTag(
                {
                  productName: productName,
                  pageType: PAGE_TYPE.BRAND,
                  brandName: props.brandName,
                },
                strictLocalize
              )
            }
            shouldIncrementallyLoadImages
            onLoad={() => TelemetryManager.get().endSpan(imageLoadSpan)}
          />
          <ProductTileOverlay
            // TODO this probably needs to be memoized
            data={productTileOverlayProductTileAdaptor(
              ILeanProductTile.build({
                product: ILeanProductTile.ILeanProduct.build({
                  state: productTile?.product?.state,
                }),
              })
            )}
          />
          {renderExtraContent()}
        </ImageContainer>
        {props.hideInfo ? null : <RetailInfo productTile={props.productTile} />}
      </ProductLink>
    </Container>
  );
};

export const ProductTile = withConfigurationConsumer(ProductTileBase);
ProductTile.displayName = "ProductTile";

const PLACEHOLDER_IMAGE = IImage.build({
  token: "p_placeholder",
  url: "https://cdn.faire.com/fastly/3f21a8e5b96b2c091abc81901eddfd258b8a5db4f3abcf99ecefb5dbde7d54b6.png",
});

const DesktopQuickAdd = styled(QuickAdd)`
  &&& {
    display: none;
  }
`;

const PlusButton = styled(RoundedPlusButton)`
  position: absolute;
  bottom: ${getSpacing("1x")};
  right: ${getSpacing("1x")};
  z-index: 3;
`;

const productLinkStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;

  ${responsiveMediaQueries.desktopAndAbove} {
    &:hover {
      ${DesktopQuickAdd} ${DesktopQuickAdd} ${DesktopQuickAdd} {
        display: flex;
      }
    }
  }
`;

const RegularProductLink = styled(Link)`
  ${productLinkStyles}
`;

// This is used so mobile can intercept the navigation event
// eslint-disable-next-line @faire/no-raw-anchor-tag
const AnchorProductLink = styled.a`
  ${productLinkStyles}
`;

const StyledSaveToBoard = styled(SaveToBoard)`
  position: relative;
`;

const Container = styled(Column)`
  position: relative;
`;
